import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Text, Group, Stack, Button, NumberInput, Badge, Loader, Center, ActionIcon, Tooltip } from '@mantine/core'
import { IconClipboardCheck, IconCheck, IconRefresh, IconMapPin } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { getPendingHkMaterials, confirmHkMaterial } from '../api/client'
import { useBreakpoint } from '../hooks/useBreakpoint'

export default function StockConfirmPage() {
  const { t } = useTranslation()
  const { isSmall } = useBreakpoint()
  const [items, setItems] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [counted, setCounted] = useState<Record<number, number>>({})
  const [confirming, setConfirming] = useState<number | null>(null)

  const load = async () => {
    setLoading(true)
    try {
      const { data } = await getPendingHkMaterials()
      setItems(data ?? [])
      // ค่าเริ่มต้น = จำนวนที่ขอ ปรับแก้ได้ตามที่นับได้จริง
      const init: Record<number, number> = {}
      for (const m of data ?? []) init[m.id] = m.qty
      setCounted(init)
    } catch {
      notifications.show({ color: 'red', message: t('stockConfirm.loadError', 'Failed to load queue') })
    } finally { setLoading(false) }
  }

  useEffect(() => { load() }, [])

  const handleConfirm = async (materialId: number) => {
    const qty = counted[materialId]
    if (!qty || qty <= 0) {
      notifications.show({ color: 'red', message: t('stockConfirm.invalidQty', 'Enter the counted quantity first') })
      return
    }
    setConfirming(materialId)
    try {
      await confirmHkMaterial(materialId, qty)
      notifications.show({ color: 'teal', message: t('stockConfirm.confirmSuccess', 'Confirmed — stock deducted') })
      setItems(prev => prev.filter(m => m.id !== materialId))
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? t('stockConfirm.confirmError', 'Confirm failed') })
    } finally { setConfirming(null) }
  }

  return (
    <Box p={isSmall ? 16 : 32}>
      <Group justify="space-between" mb="lg">
        <Box>
          <Group gap={8}>
            <IconClipboardCheck size={22} color="#0d9488" />
            <Text style={{ fontFamily: 'Inter, sans-serif', fontSize: isSmall ? 20 : 24, fontWeight: 600, letterSpacing: '-0.3px' }}>
              {t('stockConfirm.title', 'Stock Confirm Queue (ตัดพาร์)')}
            </Text>
          </Group>
          <Text size="sm" c="dimmed">
            {t('stockConfirm.subtitle', 'Count the item physically before confirming — stock is only deducted after you confirm here')}
          </Text>
        </Box>
        <Button variant="light" leftSection={<IconRefresh size={14} />} onClick={load}>
          {t('common.refresh', 'Refresh')}
        </Button>
      </Group>

      {loading ? (
        <Center py="xl"><Loader color="teal" /></Center>
      ) : items.length === 0 ? (
        <Center py="xl">
          <Stack align="center" gap={6}>
            <IconCheck size={32} color="#94a3b8" />
            <Text size="sm" c="dimmed">{t('stockConfirm.empty', 'No pending items — all caught up')}</Text>
          </Stack>
        </Center>
      ) : (
        <Stack gap="sm">
          {items.map((m: any) => (
            <Box key={m.id} style={{ background: '#fff', borderRadius: 14, border: '1px solid #ebebeb', padding: 16 }}>
              <Group justify="space-between" wrap="wrap" gap="md">
                <Box style={{ flex: 1, minWidth: 200 }}>
                  <Group gap={8} mb={4}>
                    <Text fw={600} size="sm">{m.consumableItem?.name ?? `#${m.consumableItemId}`}</Text>
                    <Badge size="xs" variant="light" color="gray">{m.hkOrder?.orderCode ?? `HKO-${m.hkOrderId}`}</Badge>
                  </Group>
                  <Text size="xs" c="dimmed">
                    {m.hkOrder?.title}{m.hkOrder?.location ? ` · ${m.hkOrder.location}` : ''}
                  </Text>
                  {m.location?.name && (
                    <Group gap={4} mt={4}>
                      <IconMapPin size={12} color="#94a3b8" />
                      <Text size="xs" c="dimmed">{m.location.name}</Text>
                    </Group>
                  )}
                </Box>

                <Group gap="sm" align="flex-end">
                  <Box>
                    <Text size="xs" c="dimmed" mb={2}>{t('stockConfirm.requested', 'Requested')}</Text>
                    <Text size="sm" fw={600} ta="center">{m.qty}</Text>
                  </Box>
                  <NumberInput
                    label={t('stockConfirm.countedQty', 'Counted qty')}
                    size="sm"
                    min={0}
                    value={counted[m.id] ?? m.qty}
                    onChange={(v) => setCounted(prev => ({ ...prev, [m.id]: Number(v) || 0 }))}
                    style={{ width: 110 }}
                    styles={{ input: { textAlign: 'center', fontWeight: 600 } }}
                  />
                  <Tooltip label={t('stockConfirm.confirmTooltip', 'Confirm and deduct stock')}>
                    <Button size="sm" loading={confirming === m.id}
                      leftSection={<IconCheck size={14} />}
                      style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #fff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}
                      onClick={() => handleConfirm(m.id)}>
                      {t('stockConfirm.confirm', 'Confirm')}
                    </Button>
                  </Tooltip>
                </Group>
              </Group>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  )
}
